const CreativeTemplate = ({ candidateData }) => {
    if (!candidateData) return null

    const {
        name,
        email,
        phone,
        basic_details,
        employment,
        education,
        projects,
        certifications,
        it_skills,
        candidate_languages,
    } = candidateData

    return (
        <div className="w-[210mm] min-h-[297mm] max-w-[210mm] mx-auto bg-white text-gray-800 shadow-md font-sans leading-normal overflow-hidden box-border relative">
            {/* Header Section */}
            <div className="bg-purple-600 text-white p-6 relative overflow-hidden">
                <div className="max-w-[90%] relative z-20">
                    <h1 className="text-2xl text-left font-bold mb-2 tracking-wide">{name}</h1>
                    <p className="text-base font-medium opacity-90">{employment?.[0]?.job_title?.title || "Professional"}</p>

                    {/* Contact info */}
                    <div className="flex flex-wrap space-x-4 ">
                        {email && (
                            <div className="flex items-center">
                                <span className="text-xs opacity-90">{email}</span>
                            </div>
                        )}
                        {phone && (
                            <div className="flex items-center">
                                <span className="text-xs opacity-90">{phone}</span>
                            </div>
                        )}
                        {(basic_details?.city?.name || basic_details?.state?.name) && (
                            <div className="flex items-center">
                                <span className="text-xs opacity-90">
                                    {basic_details?.city?.name}
                                    {basic_details?.city?.name && basic_details?.state?.name ? ", " : ""}
                                    {basic_details?.state?.name}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Decorative elements */}
                <div className="absolute right-[-40px] top-[-40px] w-45 h-45 bg-purple-500 opacity-20 rounded-full" />
                <div className="absolute right-15 bottom-[-30px] w-25 h-25 bg-purple-700 opacity-30 rounded-full" />
            </div>

            <div className="grid grid-cols-5 gap-0 min-h-[calc(297mm-130px)]">
                {/* Left sidebar - 30% width */}
                <div className="col-span-2 bg-gray-50 p-5 border-r border-gray-200">
                    {/* Profile Summary */}
                    {basic_details?.profile_summery && (
                        <div className="mb-6">
                            <h2 className="text-base font-bold text-purple-600 mb-3 pb-1 border-b-2 border-purple-200 tracking-wide uppercase">
                                About Me
                            </h2>
                            <p className="text-xs leading-relaxed text-justify">{basic_details.profile_summery}</p>
                        </div>
                    )}

                    {/* Skills */}
                    {it_skills && it_skills.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-base font-bold text-purple-600 mb-3 pb-1 border-b-2 border-purple-200 tracking-wide uppercase">
                                Skills
                            </h2>
                            <div className="flex flex-col space-y-3">
                                {it_skills.map((skill, index) => (
                                    <div key={index}>
                                        <div className="flex justify-between mb-1">
                                            <span className="text-xs font-medium">{skill.skill.title}</span>
                                            <span className="text-xs text-gray-600">
                                                {skill.experience_year}y {skill.experience_month}m
                                            </span>
                                        </div>
                                        <div className="w-full h-1 bg-gray-200 rounded-full">
                                            <div
                                                className="h-1 bg-purple-400 rounded-full"
                                                style={{
                                                    width: `${Math.min(100, skill.experience_year * 20 + skill.experience_month * 1.5)}%`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Languages */}
                    {candidate_languages && candidate_languages.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-base font-bold text-purple-600 mb-3 pb-1 border-b-2 border-purple-200 tracking-wide uppercase">
                                Languages
                            </h2>
                            <div className="flex flex-wrap gap-2">
                                {candidate_languages.map((lang, index) => (
                                    <span key={index} className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs">
                                        {lang.language}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Certifications */}
                    {certifications && certifications.length > 0 && (
                        <div>
                            <h2 className="text-base font-bold text-purple-600 mb-3 pb-1 border-b-2 border-purple-200 tracking-wide uppercase">
                                Certifications
                            </h2>
                            <div className="flex flex-col space-y-2">
                                {certifications.map((cert, index) => (
                                    <div key={index}>
                                        <p className="text-xs font-medium">{cert.certification_name}</p>
                                        {cert.certification_date && <p className="text-xs text-gray-600">{cert.certification_date}</p>}
                                        {cert.certification_authority && (
                                            <p className="text-xs text-gray-600">{cert.certification_authority}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Main content - 70% width */}
                <div className="col-span-3 p-5">
                    {/* Experience */}
                    {employment && employment.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-base font-bold text-purple-600 mb-4 pb-1 border-b-2 border-purple-200 tracking-wide uppercase">
                                Work Experience
                            </h2>
                            <div className="space-y-5">
                                {employment.map((job, index) => (
                                    <div key={index} className="relative pl-6 pb-4">
                                        {/* Timeline dot */}
                                        <div className="absolute left-0 top-0 w-2.5 h-2.5 rounded-full bg-purple-500 z-20" />
                                        {/* Timeline line */}
                                        {index < employment.length - 1 && (
                                            <div className="absolute left-1 top-2.5 bottom-0 w-0.5 bg-purple-200" />
                                        )}

                                        <h3 className="text-sm font-bold text-purple-700 mb-1">{job.job_title?.title}</h3>
                                        <div className="flex justify-between items-center mb-1 flex-wrap gap-2">
                                            <p className="font-medium text-xs">{job.current_company}</p>
                                            <span className="border border-purple-300 text-purple-700 text-xs rounded-md px-2">
                                                {job.start_month}/{job.start_year} -{" "}
                                                {job.end_month ? `${job.end_month}/${job.end_year}` : "Present"}
                                            </span>
                                        </div>
                                        <p className="text-xs mt-1 leading-relaxed text-justify">{job.job_profile}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Education */}
                    {education && education.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-base font-bold text-purple-600 mb-4 pb-1 border-b-2 border-purple-200 tracking-wide uppercase">
                                Education
                            </h2>
                            <div className="space-y-5">
                                {education.map((edu, index) => (
                                    <div key={index} className="relative pl-6 pb-4">
                                        {/* Timeline dot */}
                                        <div className="absolute left-0 top-0 w-2.5 h-2.5 rounded-full bg-purple-500" />
                                        {/* Timeline line */}
                                        {index < education.length - 1 && (
                                            <div className="absolute left-1 top-2.5 bottom-0 w-0.5 bg-purple-200" />
                                        )}

                                        <h3 className="text-sm font-bold text-purple-700 mb-1">{edu.education_course?.title}</h3>
                                        <div className="flex justify-between items-center mb-1 flex-wrap gap-2">
                                            <p className="font-medium text-xs">{edu.insitute_university}</p>
                                            <span className="border border-purple-300 text-purple-700 text-xs rounded-md px-2">
                                                {edu.course_duration_start_year} - {edu.course_duration_end_year}
                                            </span>
                                        </div>
                                        {edu.education_course_specialty && (
                                            <p className="text-xs mt-1">
                                                {edu.education_course_specialty.title}
                                                {edu.marks && ` • ${edu.marks}%`}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Projects */}
                    {projects && projects.length > 0 && (
                        <div>
                            <h2 className="text-base font-bold text-purple-600 mb-4 pb-1 border-b-2 border-purple-200 tracking-wide uppercase">
                                Projects
                            </h2>
                            <div className="space-y-5">
                                {projects.map((project, index) => (
                                    <div key={index} className="relative pl-6 pb-4">
                                        {/* Timeline dot */}
                                        <div className="absolute left-0 top-0 w-2.5 h-2.5 rounded-full bg-purple-500" />
                                        {/* Timeline line */}
                                        {index < projects.length - 1 && (
                                            <div className="absolute left-1 top-2.5 bottom-0 w-0.5 bg-purple-200" />
                                        )}

                                        <h3 className="text-sm font-bold text-purple-700 mb-1">{project.project_tag}</h3>
                                        <div className="flex justify-between items-center mb-1 flex-wrap gap-2">
                                            <p className="font-medium text-xs">{project.role}</p>
                                            <span
                                                className={`text-xs rounded-md px-2 ${project.project_status === "Ongoing"
                                                    ? "bg-green-500 text-white"
                                                    : "border border-purple-300 text-purple-700"
                                                    }`}
                                            >
                                                {project.project_status}
                                            </span>
                                        </div>
                                        <p className="text-xs mt-1 leading-relaxed text-justify">{project.project_description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CreativeTemplate
